import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Row, Col, Table, Button, notification } from "antd";
import styles from "./Payment.module.scss";
import {
  carRideIcon,
  columnSortIcon,
  exclaimationWarningCircleIcon,
  expandRowDownArrowIcon,
  expandRowRightArrowIcon,
  greenCircledCheckmark,
  wandaIcon,
} from "../../../assets/images";
import moment from "moment";
import CustomButton from "../../../common/Buttons/CustomButton";
import {
  ITransactionDetails,
  PaymentTransactionTypeIds,
  RefundTransactionId,
  defaultTransactionDetails,
} from "../../../types/PaymentTransactionTypes";
import { getTripByRequestAssignmentId } from "../../../services/RegistrationService";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import { defaultTripDetails } from "../../ServiceUsers/ServiceUsersMobile/defaultTripDetails";
import TripDetailsModal from "../../Shared/Modals/TripDetailsModal";
import RefundTransactionModal from "../../Shared/Modals/RefundTransactionModal";
import RefundedServiceInfo from "../../Shared/RefundedServiceInfo";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import { formatUTCtoLocalDate } from "../../../helpers/dateFormatters";
import { WandaTripStatus } from "../../../types/ApplicationTypes";

type Props = {
  dataSource: ITransactionDetails[];
};
interface IColumn {
  title: React.ReactNode;
  key: string;
  defaultSortOrder?: "ascend" | "descend";
  sortDirections?: Array<"ascend" | "descend">;
  sorter?: (a: ITransactionDetails, b: ITransactionDetails) => number;
  width?: number;
  render?: (record: ITransactionDetails) => React.ReactNode;
  dataIndex?: string;
}

const BillingHistoryTable: React.FC<Props> = ({ dataSource }) => {
  const [pageSize, setPageSize] = useState<number>(20);
  const [tripLeg, setTripLeg] = useState<ITripDetails[]>([defaultTripDetails]);
  const [transaction, setTransaction] = useState<ITransactionDetails>(
    defaultTransactionDetails
  );
  const [isTripIdModalVisible, setIsTripIdModalVisible] = useState<boolean>(
    false
  );
  const [
    isRefundTransactionModalVisible,
    setIsRefundTransactionModalVisible,
  ] = useState<boolean>(false);

  const { isWandaConcierge } = usePersonSummaryCtx();

  const history = useHistory();

  const handleRefund = (record: ITransactionDetails) => {
    setTransaction(record);
    setIsRefundTransactionModalVisible(true);
  };

  const renderRefundStatus = (record: ITransactionDetails) => {
    //TO DO: Display specific message for each refund transaction type for Phase-2
    switch (record.refundTransactionStatusId) {
      case RefundTransactionId.Processed:
      case RefundTransactionId.Partial:
        return (
          <Row justify="end" align="middle" className={styles.refundStatus}>
            <img src={greenCircledCheckmark} />
            Refund Success
          </Row>
        );

      case RefundTransactionId.Pending:
      case RefundTransactionId.Review:
      case RefundTransactionId.Failed:
        return (
          <Row justify="end" align="middle" className={styles.refundStatus}>
            <img src={exclaimationWarningCircleIcon} />
            Refund Pending
          </Row>
        );

      default:
        return (
          <Row justify="end" align="middle">
            <Button
              className={styles.refundBtn}
              onClick={() => handleRefund(record)}
            >
              Refund
            </Button>
          </Row>
        );
    }
  };

  const columns: Array<IColumn> = [
    {
      title: (
        <Row gutter={[6, 0]}>
          <Col>Transaction Date</Col>
          <Col>
            <img src={columnSortIcon} alt="sort" />
          </Col>
        </Row>
      ),
      key: "transactionDate",
      defaultSortOrder: "descend",
      sortDirections: ["ascend", "descend", "ascend"],
      sorter: (a: ITransactionDetails, b: ITransactionDetails): number =>
        moment(a.createdOn).unix() - moment(b.createdOn).unix(),
      width: 180,
      render: (record: ITransactionDetails) => (
        <Row>
          <Col>{formatUTCtoLocalDate(record?.createdOn, "MM/DD/YYYY")}</Col>
        </Row>
      ),
    },
    {
      title: (
        <Row justify="end">
          <Col>Amount</Col>
        </Row>
      ),
      key: "transactionAmount",
      width: 100,
      render: (record: ITransactionDetails) => (
        <Row justify="end">
          {record.paymentTransactionTypeId ===
            PaymentTransactionTypeIds.Refund && <Col>+</Col>}{" "}
          <Col>${record?.amount?.toFixed(2)}</Col>
        </Row>
      ),
    },
    {
      title: "Transaction Type",
      dataIndex: "paymentTransactionTypeName",
      key: "paymentTransactionTypeName",
      width: 150,
    },
    {
      title: "Payment Type",
      key: "paymentType",
      width: 300,
      render: (record: ITransactionDetails) => (
        <Row>
          <Col span={24}>{record?.paymentInstrumentName}</Col>
        </Row>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "paymentTransactionId",
      key: "paymentTransactionId",
      width: 240,
    },
    {
      title: "",
      key: "refund",
      width: 180,
      render: (record: ITransactionDetails) => {
        return (
          ["Service", "Subscription"].includes(
            record.paymentTransactionTypeName
          ) &&
          isWandaConcierge &&
          renderRefundStatus(record)
        );
      },
    },
  ];

  const handleTripIdAndRefundedTripId = async (record: ITransactionDetails) => {
    try {
      const apiResponse = await getTripByRequestAssignmentId({
        requestAssignmentId: record.requestAssignmentId,
        personId: record.serviceUserPersonId || record.customerPersonId,
        favoriteId: 0,
        tripDate: moment.utc(record.tripDate).format(),
      });
      setTripLeg(
        apiResponse.personTrips.filter(
          (trip: ITripDetails) => Number(trip.tripId) === record.tripId
        )
      );
      setIsTripIdModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Fetch Trips",
      });
    }
  };

  const handleBookingId = (record: ITransactionDetails) =>
    history.push(
      `/DashBoard/TransportationServiceSetupSummary/${record.serviceUserPersonId ??
        record.customerPersonId}/${record.requestAssignmentId}/${
        record.bookingId
      }/${WandaTripStatus.Completed}/${record.tripDate}`
    );

  const expandableView = (
    paymentTransactionTypeName: string,
    record: ITransactionDetails
  ) => {
    switch (paymentTransactionTypeName) {
      case "Service":
        return (
          <>
            <Col>
              <Row align="middle">
                <Col span={24} className={styles.title}>
                  Service User
                </Col>
                <Col span={24} className={styles.description}>
                  {record?.serviceUser}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row align="middle">
                <Col span={24} className={styles.title}>
                  Service Type
                </Col>
                <Col span={24} className={styles.description}>
                  <img src={carRideIcon} alt="ride" className={styles.icon} />
                  {record?.serviceType}
                </Col>
              </Row>
            </Col>
            <Col>
              <Row align="middle">
                <Col span={24} className={styles.title}>
                  Trip ID
                </Col>
                <Col span={24} className={styles.hyperlink}>
                  <CustomButton
                    text={record?.tripId?.toString()}
                    type="link"
                    handlerFunction={() =>
                      handleTripIdAndRefundedTripId(record)
                    }
                    className="hyperLinkBtn"
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <Row align="middle">
                <Col span={24} className={styles.title}>
                  Booking ID
                </Col>
                <Col span={24} className={styles.hyperlink}>
                  <CustomButton
                    text={record?.bookingId?.toString()}
                    type="link"
                    handlerFunction={() => handleBookingId(record)}
                    className="hyperLinkBtn"
                  />
                </Col>
              </Row>
            </Col>
          </>
        );

      case "Subscription":
        return (
          <>
            <Col span={24} className={styles.title}>
              Subscription Type
            </Col>
            <Col span={24} className={styles.description}>
              <img
                src={wandaIcon}
                className={styles.icon}
                alt="wandaPlusSubscription"
              />
              Wanda Plus
            </Col>
          </>
        );

      case "Refund":
        return (
          <>
            <Col>
              {record?.details && (
                <Row align="middle">
                  <Col span={24} className={styles.title}>
                    Refund Details
                  </Col>
                  <Col span={24} className={styles.description}>
                    {record?.details}
                  </Col>
                </Row>
              )}
            </Col>
            <Col>
              {record?.tripId && (
                <Row align="middle">
                  <Col span={24} className={styles.title}>
                    Refunded Trip ID
                  </Col>
                  <Col span={24} className={styles.hyperlink}>
                    <CustomButton
                      text={record?.tripId?.toString()}
                      type="link"
                      handlerFunction={() =>
                        handleTripIdAndRefundedTripId(record)
                      }
                      className="hyperLinkBtn"
                    />
                  </Col>
                </Row>
              )}
            </Col>
            <Col>
              {record?.refundedTransactionId && (
                <Row align="middle">
                  <Col span={24} className={styles.title}>
                    Refunded Transaction ID
                  </Col>
                  <Col span={24} className={styles.description}>
                    {record?.refundedTransactionId}
                  </Col>
                </Row>
              )}
            </Col>
          </>
        );

      // TO-DO: Wanda Cash for Phase-2

      // case "Credit":
      //   return (
      //     <>
      //       <Col span={24} className={styles.title}>
      //         Credit Details
      //       </Col>
      //       <Col span={24} className={styles.description}>
      //         $5.00 has been added to your Wanda Cash balance.
      //       </Col>
      //     </>
      //   );
    }
  };

  function customExpandIcon(props: {
    expanded: any;
    onExpand: (
      arg0: any,
      arg1: React.MouseEvent<HTMLImageElement, MouseEvent>
    ) => void;
    record: any;
  }) {
    return (
      <img
        onClick={(e) => {
          props.onExpand(props.record, e);
        }}
        src={props.expanded ? expandRowDownArrowIcon : expandRowRightArrowIcon}
        alt="expand row"
      />
    );
  }

  const handlePageSizeChange = (current: number, size: number) =>
    setPageSize(size);

  const paginationConfig = {
    pageSize: pageSize,
    onShowSizeChange: handlePageSizeChange,
  };

  return (
    <>
      <Table
        rowKey="paymentTransactionId"
        columns={columns}
        dataSource={dataSource}
        pagination={paginationConfig}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }: any) =>
            customExpandIcon({ expanded, onExpand, record }),
          expandedRowRender: (record) => (
            <>
              {record?.paymentTransactionTypeId ===
                PaymentTransactionTypeIds.Service &&
                [
                  RefundTransactionId.Processed,
                  RefundTransactionId.Partial,
                ].includes(record?.refundTransactionStatusId) && (
                  <RefundedServiceInfo
                    refundedTransactionId={record?.refundTransactionId}
                    refundedAmount={record?.amount}
                    refundedDate={record?.editedOn}
                  />
                )}

              <Row className={styles.expandedViewContent} align="middle">
                {expandableView(record.paymentTransactionTypeName, record)}
              </Row>
            </>
          ),
        }}
      />

      <TripDetailsModal
        visible={isTripIdModalVisible}
        setVisibility={setIsTripIdModalVisible}
        trip={tripLeg}
      />

      <RefundTransactionModal
        visible={isRefundTransactionModalVisible}
        setVisibility={setIsRefundTransactionModalVisible}
        transaction={transaction}
      />
    </>
  );
};

export default BillingHistoryTable;
